import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector, useDispatch } from 'react-redux';
import './EditSectionLayout.scss';
import useActiveSection from '../../../features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/useActiveSection';
import generateLayoutTypeChangeHandler from '../../../features/analyses/Freeform/FreeformAnalysisLayout/generateLayoutTypeChangeHandler';
import {
  getReportFormatLayoutState,
  toggleRightPanel2,
} from '../../redux/reportFormat/reportFormatSlice';
import RightPanel, { LAYOUTS_BY_TYPE } from '../LayoutsRightPanel/RightPanel';
import LeftPanel from './LeftPanel/LeftPanel';
import { useSections } from './SectionContext';
import {
  setCurrentSections,
  setNonLaidOutLibraryItems,
} from './SectionContextUtils';
import useMobile from '../../hooks/useMobile';
import { LostCommentsWarningDialog } from '../LostCommentsWarningDialog';

const EditSectionLayout = ({
  showRightPanel = false,
  libraryItemMap,
  droppableLocations,
  onAddNewSection,
  setDroppableLocations,
  onDragStart,
  onDragUpdate,
  onDragEnd,
  loading = false,
}) => {
  const [
    {
      currentSections = [],
      nonLaidOutLibraryItems = [],
      rightPanelLayoutType = LAYOUTS_BY_TYPE,
    },
    contextDispatch,
  ] = useSections();
  const dispatch = useDispatch();
  const bottomPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const [renderedSectionElements, setRenderedSectionElements] = useState(null);
  const { activeSectionIndex } = useSelector(getReportFormatLayoutState);
  const isMobile = useMobile();
  const sectionElements = Array.from(
    document.querySelectorAll('.edit-layout__left .section')
  );
  let activeLayoutId;
  if (currentSections[activeSectionIndex]) {
    activeLayoutId = currentSections[activeSectionIndex].layoutType;
  }

  const [showLostCommentsModal, setShowLostCommentsModal] = useState(false);
  const selectedLayoutIdRef = useRef();

  useActiveSection(renderedSectionElements, bottomPanelRef, rightPanelRef);

  useEffect(() => {
    if (sectionElements && !isEqual(sectionElements, renderedSectionElements)) {
      setRenderedSectionElements(sectionElements);
    }
  }, [sectionElements, renderedSectionElements]);

  const updateCurrentSections = (sections) => {
    setCurrentSections(sections, contextDispatch);
  };
  const editNonLaidOutLibraryItems = (items) => {
    setNonLaidOutLibraryItems(items, contextDispatch);
  };
  const handleLayoutTypeChange = generateLayoutTypeChangeHandler(
    activeSectionIndex,
    currentSections,
    setDroppableLocations,
    updateCurrentSections,
    nonLaidOutLibraryItems,
    editNonLaidOutLibraryItems
  );
  // TODO: remove once PWC-6524 is done
  window.handleLayoutTypeChange = handleLayoutTypeChange;

  const handleRightPanelCollapse = () => {
    dispatch(toggleRightPanel2());
  };

  return (
    <div
      className={classNames('edit-layout2', {
        'edit-layout--collapsed-column': !isMobile,
        'edit-layout--mobile': isMobile,
      })}
      data-testid="edit-layout"
    >
      <DragDropContext
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        <div className="edit-layout__left" data-testid="edit-layout-left-panel">
          <LeftPanel
            libraryItemMap={libraryItemMap}
            droppableLocations={droppableLocations}
            onAddNewSection={onAddNewSection}
            setDroppableLocations={setDroppableLocations}
            showRightPanel={showRightPanel}
            loading={loading}
          />
        </div>

        {showRightPanel && (
          <RightPanel
            handleCollapse={handleRightPanelCollapse}
            handleLayoutTypeChange={handleLayoutTypeChange}
            layouts={rightPanelLayoutType}
            activeLayoutId={activeLayoutId}
            setShowLostCommentsModal={setShowLostCommentsModal}
            selectedLayoutIdRef={selectedLayoutIdRef}
          />
        )}
      </DragDropContext>

      <LostCommentsWarningDialog
        open={showLostCommentsModal}
        onConfirm={() => {
          setShowLostCommentsModal(false);
          handleLayoutTypeChange(selectedLayoutIdRef.current);
        }}
        onClose={() => {
          setShowLostCommentsModal(false);
        }}
      />
    </div>
  );
};

export default EditSectionLayout;
